import { yupResolver } from "@hookform/resolvers/yup"
import { Typography } from "@mui/material"
import { useMutation, useQuery } from "@tanstack/react-query"
import {
  CreateContactUsPublicRequest,
  getLostFoundTrip,
  postContactsUsCreate,
} from "api"
import "dayjs/locale/he"
import { useLocale, useTranslations } from "next-intl"
import { Dispatch, LegacyRef, SetStateAction, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { useForm } from "react-hook-form"
import { MarketingConsent } from "ui"
import {
  applicationRequestError,
  applicationRequestSuccess,
  useFocusOnError,
} from "utils"
import { commonSchema, messagesInvalid } from "utils/FormSchema"
import * as yup from "yup"
import { FormId } from "../MainForm"
import {
  CustomAutocomplete,
  CustomTextField,
  DateController,
  Header,
  MultilineCustomTextField,
  NumbersController,
  RECAPTCHA_KEY,
  formatDate,
  isFieldRequired,
  notAfterNowIfToday,
  prefixOptionsArray,
} from "./Common"
import { FormContainer, InputsGrid, StyledButton } from "./styles"

export default function Complaint({
  handleResult,
  locale,
  contactType,
  setIsLoading,
  label,
  tooltipText,
}: {
  handleResult: (result: "yes" | "no", id?: string) => void
  locale: "he" | "en" | "ar" | "ru"
  contactType: FormId
  label: string
  setIsLoading: Dispatch<SetStateAction<boolean>>
  tooltipText: string
}) {
  const [isPending, setIsPending] = useState(false)
  const [shilut, setShilut] = useState<string>()
  const t = useTranslations("Forms")
  const locale1 = useLocale()
  const tInner = useTranslations("InnerPages")
  const reCaptchaRef = useRef<ReCAPTCHA>(null)
  const schema = yup.object({
    firstName: commonSchema.common(locale).firstName,
    lastName: commonSchema.common(locale).lastName,
    customerId: commonSchema.common(locale).customerId.when("ravKavAccount", {
      is: (value: string | undefined) => !value || value === "",
      then: (schema) => schema.required(messagesInvalid[locale].customerId),
      otherwise: (schema) => schema.optional(),
    }),
    ravKavAccount: commonSchema.common(locale).ravkav,
    email: commonSchema.common(locale).email,
    cellphone: commonSchema.common(locale).phonePrefix,
    phone: commonSchema.common(locale).phone,
    eventDate: commonSchema.common(locale).eventDate,
    driverFirstName: commonSchema.common(locale).driverFirstName,
    driverLastName: commonSchema.common(locale).driverLastName,
    driverPersonalNum: commonSchema.common(locale).driverPersonalNumber,
    licenseNum: commonSchema.common(locale).licenseNum,
    lineNum: yup
      .number()
      .transform((value) =>
        isNaN(value) || value === null || value === undefined
          ? undefined
          : value,
      )
      .required(messagesInvalid[locale].lineNumberRequired),
    dropDestination: commonSchema.common(locale).dropOffDestination,
    time: commonSchema
      .common(locale)
      .exactEventTime.test(
        "notAfterNowIfToday",
        messagesInvalid[locale].timeNotInFuture,
        function (value) {
          const { eventDate } = this.parent
          return notAfterNowIfToday(value, eventDate)
        },
      ),
    location: commonSchema.common(locale).eventLocation,
    incidentArea: commonSchema.common(locale).incidentArea,
    content: commonSchema.common(locale).inquiryDescription,
    mailingConsent: yup.boolean().optional(),
  })
  type FormData = yup.InferType<typeof schema>

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitted, isValid },
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      cellphone: prefixOptionsArray[0].value,
    },
  })

  // Add focus management for validation errors
  useFocusOnError(errors, isSubmitted)

  const eventDate = watch("eventDate")
  const ravKavAccount = watch("ravKavAccount")
  const { data, isLoading } = useQuery({
    queryKey: ["lostFoundTrip", eventDate, shilut],
    queryFn: async () => {
      try {
        if (!shilut || !eventDate) return []
        const res = await getLostFoundTrip(
          Number(shilut),
          eventDate.toISOString(),
        )
        return res.data.body
      } catch (error) {
        console.log(error)
      }
    },
  })

  const { mutate } = useMutation({
    mutationFn: postContactsUsCreate,
    onMutate: () => {
      setIsLoading(true)
    },
    onSuccess: (data) => {
      if (data.data.errorCode) {
        applicationRequestError(tInner("submitInquiry"), label)
        handleResult("yes")
      } else {
        applicationRequestSuccess(tInner("submitInquiry"), label)
        handleResult("no", data.data.body.caseNumber)
      }
    },
    onError: () => {
      applicationRequestError(tInner("submitInquiry"), label)
      handleResult("yes")
    },
    onSettled: () => {
      setIsLoading(false)
      setIsPending(false)
    },
  })

  const onSubmit = async (formData: FormData) => {
    setIsPending(true)
    const eventDate = formatDate(formData.eventDate, formData.time)
    reCaptchaRef?.current?.reset()
    const recaptchaResponse =
      (await reCaptchaRef?.current?.executeAsync()) as string

    // Convert eggedMakat8 back to shilut for the API
    const selectedTrip = data?.find(
      (el) => el.eggedMakat8 === formData.lineNum,
    )
    const lineNum = selectedTrip?.shilut ?? String(formData.lineNum)

    const formattedData = {
      ...formData,
      lineNum,
      eventDate,
      tripDestination: "",
      contactTypeId: contactType,
      recaptchaResponse,
      sid: window?.__ctm?.config?.sid || "-1",
    }

    mutate(formattedData as unknown as CreateContactUsPublicRequest)
  }
  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <Header />
      <InputsGrid>
        <CustomTextField
          name="firstName"
          control={control}
          label={t("firstName")}
          required={isFieldRequired(schema, "firstName")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="lastName"
          control={control}
          label={t("lastName")}
          required={isFieldRequired(schema, "lastName")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="email"
          control={control}
          label={t("email")}
          required={isFieldRequired(schema, "email")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <NumbersController
          prefixName="cellphone"
          phoneName="phone"
          control={control}
          label={t("mobileNumber")}
          required={isFieldRequired(schema, "phone")}
          isSubmitted={isSubmitted}
          errors={errors}
          locale={locale}
        />
        <CustomTextField
          name="customerId"
          control={control}
          label={t("idCard")}
          required={!ravKavAccount || ravKavAccount === ""}
          isSubmitted={isSubmitted}
          errors={errors}
        />
      </InputsGrid>

      <Typography variant="h4">{tInner("moreDetails2")}</Typography>
      <InputsGrid>
        <DateController
          locale={locale}
          name="eventDate"
          control={control}
          label={t("eventDate")}
          required={isFieldRequired(schema, "eventDate")}
          isSubmitted={isSubmitted}
          errors={errors}
          midnightText={tInner("afterMidnight")}
          okButtonLabel={tInner("okButtonLabel")}
          cancelButtonLabel={tInner("cancelButtonLabel")}
          toolbarTitle={tInner("toolbarTitle")}
        />
        <CustomTextField
          name="time"
          control={control}
          label={t("exactEventTime")}
          required={isFieldRequired(schema, "time")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomAutocomplete
          control={control}
          label={t("lineNumber")}
          name="lineNum"
          onInputChange={(value) => {
            setShilut(value)
          }}
          required={isFieldRequired(schema, "lineNum")}
          options={
            data?.map((el) => ({
              label: `קו ${el.shilut} מתחנת ${el.originYeshuvName} לתחנת ${el.destYeshuvName}`,
              value: el.eggedMakat8,
            })) || []
          }
          disabled={!eventDate}
          loading={isLoading}
          errors={errors}
          isSubmitted={isSubmitted}
          loadingText={tInner("loadingText")}
          noOptionsText={tInner("noOptionsText")}
          startTypingText={messagesInvalid[locale].lineNumber}
        />
        <CustomTextField
          name="dropDestination"
          control={control}
          label={t("dropOffStation")}
          required={isFieldRequired(schema, "dropDestination")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="incidentArea"
          control={control}
          label={t("settlement")}
          required={isFieldRequired(schema, "incidentArea")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="driverFirstName"
          control={control}
          label={t("driverFirstName")}
          required={isFieldRequired(schema, "driverFirstName")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="driverLastName"
          control={control}
          label={t("driverLastName")}
          required={isFieldRequired(schema, "driverLastName")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="driverPersonalNum"
          control={control}
          label={t("driverPersonalNumber")}
          required={isFieldRequired(schema, "driverPersonalNum")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="licenseNum"
          control={control}
          label={t("licenseNumber")}
          required={isFieldRequired(schema, "licenseNum")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="location"
          control={control}
          label={t("eventLocation")}
          required={isFieldRequired(schema, "location")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="ravKavAccount"
          control={control}
          label={t("ravKav")}
          required={isFieldRequired(schema, "ravKavAccount")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
      </InputsGrid>
      <MultilineCustomTextField
        name="content"
        control={control}
        label={t("inquiryDescription")}
        required={isFieldRequired(schema, "content")}
        isSubmitted={isSubmitted}
        errors={errors}
      />
      <MarketingConsent
        name="mailingConsent"
        control={control}
        tooltipText={tooltipText}
      />
      <StyledButton variant="primary" type="submit" disabled={isPending}>
        {tInner("submitInquiry")}
      </StyledButton>
      {RECAPTCHA_KEY && (
        <ReCAPTCHA
          ref={reCaptchaRef as LegacyRef<ReCAPTCHA>}
          sitekey={RECAPTCHA_KEY}
          size="invisible"
          hl="iw"
          tabIndex={-1}
        />
      )}
    </FormContainer>
  )
}
